import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const ExchangeRateAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/exchange-rates", token);

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "exchange-rate-add"],
    "shop/currencies/?page_size=all",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
  );

  const [selectedShop, setSelectedShop] = useState("");
  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [rateDate, setRateDate] = useState("");
  const shopCurrencies = useMemo(() => {
    if (!selectedShop) return currencies;
    return currencies.filter(
      (currency) => String(currency?.shop) === String(selectedShop)
    );
  }, [currencies, selectedShop]);

  const handleSubmit = () => {
    const payload = {
      shop: selectedShop,
      from_currency: fromCurrency,
      to_currency: toCurrency,
      rate,
      rate_date: rateDate,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop("");
      setFromCurrency("");
      setToCurrency("");
      setRate("");
      setRateDate("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("exchangeRate.addExchangeRate")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("exchangeRate.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("exchangeRate.fromCurrency")}
            fullWidth
            required
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shopCurrencies.map((currency) => (
                <MenuItem key={currency.id} value={String(currency.id)}>
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("exchangeRate.toCurrency")}
            fullWidth
            required
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shopCurrencies.map((currency) => (
                <MenuItem
                  key={currency.id}
                  value={String(currency.id)}
                  disabled={String(currency.id) === String(fromCurrency)}
                >
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("exchangeRate.rate")}
            type="number"
            inputProps={{ step: "0.000001", min: "0" }}
            fullWidth
            required
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("exchangeRate.rateDate")}
            type="date"
            fullWidth
            required
            value={rateDate}
            onChange={(e) => setRateDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("exchangeRate.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !fromCurrency ||
            !toCurrency ||
            fromCurrency === toCurrency ||
            !rate ||
            !rateDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("exchangeRate.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExchangeRateAdd;
